import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { TailSpin } from "react-loader-spinner";
import { draftTitle } from "../../../app/draftHelper";
import { useAppDispatch } from "../../../app/hooks";
import {
	convertRteAnnotationsToHighlights,
	countRteWords,
	RteContent,
	setRteProps,
} from "../../../app/rteUtils";
import { setSharedAlert } from "../../../app/siteSlice";
import { countWords } from "../../../app/utils";
import { AlertLevel } from "../../../components/AlertMessage";
import ButtonWithConfirm from "../../../components/ButtonWithConfirm";
import RichTextWrapper, { Props as RteProps } from "../../../rte/ExternalApi";
import { toStf } from "../../../rte/ScorableTextFormat";
import { ToolbarOptions } from "../../../rte/Toolbar";
import AutosaveMessage, { AutosaveStatus } from "../AutosaveMessage";
import {
	WriteDraftViewModel,
	SaveDraftResponse,
	useAutosaveDraftMutation,
	useSaveDraftMutation,
	useSubmitDraftMutation,
	useQuitAfterSavingDraftMutation,
	SaveDraftModel,
	SubmitDraftResponse,
	SubmitDraftModel,
} from "./draftingApiSlice";

interface DraftEditorProps {
	className?: string;
	draft: WriteDraftViewModel;
	draftEditorSection: React.MutableRefObject<HTMLDivElement | null>;
	fullSize: boolean;
	timeExpired: boolean;
	onSaveError: (resp: SaveDraftResponse) => void;
	onAutosaveFailure: (resp: SaveDraftResponse) => void;
	onSubmitError: (resp: SaveDraftResponse) => void;
}

export default function DraftEditor(props: DraftEditorProps) {
	const autosaveSeconds = 60;
	const [autosaveStatus, setAutosaveStatus] = useState(AutosaveStatus.Disabled);
	const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(true);
	const [wordCount, setWordCount] = useState<number>(() =>
		countWords(props.draft.responseContent)
	);
	const [disabled, setDisabled] = useState(false);
	const [loading, setLoading] = useState(false);
	const [saveDraftModel] = useState(() => initializeDraft(props.draft));

	const draftEditor = useRef<RichTextWrapper | null>(null);
	const sourcesEditor = useRef<RichTextWrapper | null>(null);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [callAutosaveDraft, callAutosaveDraftResult] = useAutosaveDraftMutation();
	const [callSaveDraft, callSaveDraftResult] = useSaveDraftMutation();
	const [callSubmitDraft, callSubmitDraftResult] = useSubmitDraftMutation();
	const [quitAfterSaving] = useQuitAfterSavingDraftMutation();

	useEffect(() => {
		if (props.timeExpired) {
			forceSubmit();
		}
	}, [props.timeExpired]);

	useEffect(() => {
		draftEditor.current?.setState({ readonly: disabled });
		sourcesEditor.current?.setState({ readonly: disabled });
	}, [disabled]);

	const toolbarOptions: ToolbarOptions = {
		position: "bottom",
		textEditing: true,
		highlights: "edit",
		highlightsShown: true,
	};

	function initializeDraft(data: WriteDraftViewModel): SaveDraftModel {
		console.log(`${new Date().toISOString()}: initializeDraft`);
		return {
			draftId: data.draftId,
			title: draftTitle(data.draftTitle),
			responseContent: data.responseContent,
			responseMarkup: data.responseMarkup,
			responseHighlights: data.responseHighlights,
			sourcesContent: data.sourcesContent,
			sourcesMarkup: data.sourcesMarkup,
			sourcesHighlights: data.sourcesHighlights,
			promptHighlights: data.promptHighlights,
		};
	}

	const extractRteContent = () => {
		if (!draftEditor.current) return;
		if (!sourcesEditor.current) return;

		const draftStf = toStf(
			draftEditor.current.state.editor,
			draftEditor.current.state.annotations
		);
		saveDraftModel.responseContent = draftStf.text;
		saveDraftModel.responseMarkup = draftStf.markup;
		saveDraftModel.responseHighlights = JSON.stringify(
			convertRteAnnotationsToHighlights(draftStf.annotations)
		);

		const sourcesStf = toStf(
			sourcesEditor.current.state.editor,
			sourcesEditor.current.state.annotations
		);
		saveDraftModel.sourcesContent = sourcesStf.text;
		saveDraftModel.sourcesMarkup = sourcesStf.markup;
		saveDraftModel.sourcesHighlights = JSON.stringify(
			convertRteAnnotationsToHighlights(sourcesStf.annotations)
		);
	};

	function handleTitleChanged(event: React.FormEvent<HTMLTextAreaElement>) {
		saveDraftModel.title = event.currentTarget.value;
	}

	function handleEssayContentChanged() {
		if (!draftEditor.current) return;

		setWordCount(countRteWords(draftEditor.current));
	}

	function handleSaveAndQuit(event: React.MouseEvent<HTMLButtonElement>) {
		const onSuccess = () => {
			quitAfterSaving(props.draft.draftId).then(() => {
				navigate("/home");
			});
		};
		const onError = () => {
			setIsAutosaveEnabled(true);
			console.error(`handleSaveAndQuit: an error occurred saving the draft`);
		};

		setIsAutosaveEnabled(false);
		saveDraft(onSuccess, onError);
	}

	function handlePrevious(event: React.MouseEvent<HTMLButtonElement>) {
		const onSuccess = () => {
			if (!props.draft.allowPrewriting) {
				navigate(`/home`);
			} else if (!props.draft.organizer.htmlTemplate) {
				navigate(`/writing/organizers?draftId=${props.draft.draftId}`);
			} else {
				navigate(`/writing/organize?draftId=${props.draft.draftId}`);
			}
		};
		const onError = () => {
			console.error(`handlePrevious: an error occurred saving the draft`);
		};

		setIsAutosaveEnabled(false);
		saveDraft(onSuccess, onError);
	}

	const saveDraft = (onSuccess: () => void, onError: () => void) => {
		extractRteContent();

		callSaveDraft(saveDraftModel)
			.unwrap()
			.then((payload) => {
				onSuccess();
			})
			.catch((error) => {
				onError();
			});
	};

	function handleSubmit() {
		const onSuccess = () => {
			navigate(`/scoreReport?draftId=${props.draft.draftId}`);
		};
		const onFailure = (resp: SaveDraftResponse) => {
			// handle submit failure (PEG error, etc.)
			console.error(`submit failure - status: ${resp.status}, message: ${resp.message}`);
			props.onSubmitError(resp);
			setIsAutosaveEnabled(true);
		};
		const onError = (error: any) => {
			// handle submit error (bad HTTP status)
			console.error(`submit error - error: ${JSON.stringify(error)}`);
			props.onSubmitError({ status: "error", message: JSON.stringify(error) });
			setIsAutosaveEnabled(true);
		};

		setIsAutosaveEnabled(false);
		submitDraft(false, onSuccess, onFailure, onError);
	}

	function forceSubmit() {
		const loadScoreReport = () => {
			navigate(`/scoreReport?draftId=${props.draft.draftId}`);
		};
		const onSuccess = () => {
			loadScoreReport();
		};
		const onFailure = (resp: SaveDraftResponse) => {
			// handle submit failure (PEG error, etc.)
			const errorMessage = `Time expired while you were working on the essay.  It was saved, but could not be scored for the following reason: ${resp.message}`;
			console.error(errorMessage);
			dispatch(setSharedAlert({ level: AlertLevel.Error, message: errorMessage }));
			props.onSubmitError(resp);
			loadScoreReport();
		};
		const onError = (error: any) => {
			// handle submit error (bad HTTP status)
			const errorMessage = `Time expired while you were working on the essay, but it could not be submitted for the following reason: ${JSON.stringify(error)}`;
			console.error(errorMessage);
			dispatch(setSharedAlert({ level: AlertLevel.Error, message: errorMessage }));
			props.onSubmitError({ status: "error", message: JSON.stringify(error) });
			loadScoreReport();
		};

		setIsAutosaveEnabled(false);
		submitDraft(true, onSuccess, onFailure, onError);
	}

	const submitDraft = (
		force: boolean,
		onSuccess: () => void,
		onFailure: (resp: SubmitDraftResponse) => void,
		onError: (error: any) => void
	) => {
		console.log(`${new Date().toISOString()}: submitDraft`);

		extractRteContent();

		const submit: SubmitDraftModel = { ...saveDraftModel, force };

		setDisabled(true);
		setLoading(true);
		document.getElementById("draft-rte-editor")?.classList.add("bg-black/10");
		document.getElementById("sources-rte-editor")?.classList.add("bg-black/10");
		document.getElementById("draft-title")?.classList.add("bg-black/10");

		callSubmitDraft(submit)
			.unwrap()
			.then((payload) => {
				if (payload.status == "success") {
					onSuccess();
				} else {
					resetEditors();
					onFailure(payload);
				}
			})
			.catch((error) => {
				if (error?.status == 400 && error.data?.status && error.data.message) {
					resetEditors();
					onFailure(error.data);
				} else {
					resetEditors();
					onError(error);
				}
			});
	};

	const resetEditors = () => {
		setDisabled(false);
		setLoading(false);
		document.getElementById("draft-rte-editor")?.classList.remove("bg-black/10");
		document.getElementById("sources-rte-editor")?.classList.remove("bg-black/10");
		document.getElementById("draft-title")?.classList.remove("bg-black/10");
	};

	useEffect(() => {
		var lastAutosaveTime = Date.now();

		if (!isAutosaveEnabled) {
			setAutosaveStatus(AutosaveStatus.Disabled);
			return;
		}

		setAutosaveStatus(AutosaveStatus.Waiting);

		const interval = setInterval(() => {
			let autosaveTime = Date.now();
			console.log(
				`${new Date().toISOString()}: autosave delay: ${
					(autosaveTime - lastAutosaveTime) / 60
				} seconds`
			);
			if (autosaveTime - lastAutosaveTime > (autosaveSeconds + 5) * 1000) {
				console.error(
					`Captain, there is disturbance in the temporal axis! ${
						(autosaveTime - lastAutosaveTime) / 1000
					} seconds`
				);
			}
			lastAutosaveTime = autosaveTime;
			handleAutosave();
		}, autosaveSeconds * 1000);

		return () => {
			clearInterval(interval);
			setAutosaveStatus(AutosaveStatus.Disabled);
		};
	}, [isAutosaveEnabled]);

	const handleAutosave = () => {
		const onAutosaveSuccess = () => {
			setAutosaveStatus(AutosaveStatus.Waiting);
			setDisabled(false);
		};
		const onAutosaveFailure = (resp: SaveDraftResponse) => {
			// handle autosave failure (draft deleted, wrong writer, etc.)
			console.error(`save failure - status: ${resp.status}, message: ${resp.message}`);
			setDisabled(true);
			setIsAutosaveEnabled(false);
			props.onAutosaveFailure(resp);
		};
		const onAutosaveError = (error: any) => {
			// handle autosave error
			setAutosaveStatus(AutosaveStatus.Error);
			setDisabled(false);
			props.onSaveError({ status: "error", message: JSON.stringify(error) });
		};

		console.log(`${new Date().toISOString()}: handleAutosave`);
		setAutosaveStatus(AutosaveStatus.Saving);
		autosaveDraft(onAutosaveSuccess, onAutosaveFailure, onAutosaveError);
	};

	const autosaveDraft = (
		onSuccess: () => void,
		onFailure: (resp: SaveDraftResponse) => void,
		onError: (error: any) => void
	) => {
		extractRteContent();

		callAutosaveDraft(saveDraftModel)
			.unwrap()
			.then((payload) => {
				if (payload.status == "success") {
					onSuccess();
				} else {
					onFailure(payload);
				}
			})
			.catch((error) => {
				if (error?.status == 400 && error.data?.status && error.data.message) {
					onFailure(error.data);
				} else {
					onError(error);
				}
			});
	};

	const responseProps: RteProps = {
		id: "draft-rte-editor",
		readonly: false,
		disablePasteDrop: !props.draft.allowCopyPaste,
		characterLimit: 25000,
		deleteThreshold: props.draft.bigChangeThreshold,
		toolbarOptions: toolbarOptions,
		onDocumentChanged: handleEssayContentChanged,
	};

	const responseContent: RteContent = {
		format: props.draft.responseFormat,
		content: props.draft.responseContent,
		markup: props.draft.responseMarkup,
	};

	setRteProps(responseProps, responseContent, JSON.parse(props.draft.responseHighlights), []);

	const sourcesProps: RteProps = {
		id: "sources-rte-editor",
		readonly: false,
		disablePasteDrop: !props.draft.allowCopyPaste,
		characterLimit: 25000,
		deleteThreshold: props.draft.bigChangeThreshold,
		toolbarOptions: toolbarOptions,
	};

	const sourcesContent: RteContent = {
		format: props.draft.sourcesFormat,
		content: props.draft.sourcesContent,
		markup: props.draft.sourcesMarkup,
	};

	setRteProps(sourcesProps, sourcesContent, JSON.parse(props.draft.sourcesHighlights), []);

	return (
		<section className={props.className}>
			<div
				id="loading-spinner"
				className={
					loading
						? `${props.fullSize ? "w-2/3" : "w-1/3"} absolute flex h-screen items-center justify-center`
						: "none"
				}>
				<TailSpin
					visible={loading}
					height="80"
					width="80"
					color="#4fa94d"
					ariaLabel="tail-spin-loading"
					radius="1"
					wrapperClass=" bg-gray-300 border-gray-300 rounded-lg p-4 z-10"
				/>
			</div>

			<div ref={props.draftEditorSection}>
				<textarea
					id="draft-title"
					name="draftTitle"
					maxLength={150}
					spellCheck={false}
					placeholder="Title"
					readOnly={disabled}
					className="mt-2 mb-2 resize-none w-full h-10 text-sm rounded-sm border-gray-300"
					defaultValue={draftTitle(props.draft.draftTitle)}
					onChange={handleTitleChanged}></textarea>
				<div id="response-editor" className="responseEditor">
					<RichTextWrapper ref={draftEditor} {...responseProps} />
				</div>
				<h4 className="mt-4">Cited Sources</h4>
				<div id="sources-editor" className="sourcesEditor">
					<RichTextWrapper ref={sourcesEditor} {...sourcesProps} />
				</div>
				<div className="flex justify-between mt-4">
					<AutosaveMessage
						onClick={handleAutosave}
						status={autosaveStatus}
						interval={autosaveSeconds}
						className="transition-opacity ease-in duration-1000"
					/>

					<div>{`Word count: ${wordCount}`}</div>
				</div>
			</div>
			<div className="flex justify-between mt-8">
				<div>
					{props.draft.allowPrewriting && (
						<button
							id="organizer-button"
							className="btn btn-secondary"
							onClick={handlePrevious}
							disabled={disabled}>
							Organizer
						</button>
					)}
				</div>
				<div>
					<button
						id="save-and-quit-draft-button"
						className="btn btn-secondary mr-2"
						onClick={handleSaveAndQuit}
						disabled={disabled}>
						Save and Quit
					</button>

					<ButtonWithConfirm
						id="submit-draft-button"
						className="btn btn-primary"
						disabled={disabled}
						message="Are you sure you want to submit your essay?"
						type={AlertLevel.Warning}
						confirmLabel="Yes"
						cancelLabel="Cancel"
						onConfirm={handleSubmit}>
						Submit
					</ButtonWithConfirm>
				</div>
			</div>
		</section>
	);
}
